/**
 * Recruiter API
 *
 * RTK Query endpoints for recruiter/company functionality:
 * - Saved candidates (favorites)
 * - Evaluation sessions
 * - Contact requests
 */

import { api } from '../api';

// Types
export type EvaluationSessionStatus = 'active' | 'completed' | 'cancelled';
export type EvaluationDecision = 'pending' | 'interested' | 'not_interested' | 'maybe';
export type ContactRequestStatus = 'pending' | 'accepted' | 'declined' | 'expired';

export interface SavedCandidate {
  id: string;
  candidateUserId: string;
  displayName: string | null;
  headline: string | null;
  avatarUrl: string | null;
  publicSlug: string | null;
  note: string | null;
  tags: string[];
  savedAt: string;
}

export interface SavedCandidatesResponse {
  items: SavedCandidate[];
  total: number;
}

export interface SaveCandidateRequest {
  candidateUserId: string;
  note?: string;
  tags?: string[];
}

export interface EvaluationSession {
  id: string;
  recruiterId: string;
  title?: string;
  description?: string;
  status: EvaluationSessionStatus;
  totalCreditsUsed: number;
  itemCount: number;
  filters?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface EvaluationItem {
  id: string;
  sessionId: string;
  candidateUserId: string;
  decision: EvaluationDecision;
  notes?: string;
  creditsUsed: number;
  highlightsViewed?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface EvaluationSessionsResponse {
  items: EvaluationSession[];
  total: number;
}

export interface EvaluationSessionWithItems {
  session: EvaluationSession;
  items: EvaluationItem[];
}

export interface CreateEvaluationSessionRequest {
  title?: string;
  description?: string;
  filters?: Record<string, unknown>;
}

export interface AddCandidateToEvaluationRequest {
  sessionId: string;
  candidateUserId: string;
}

export interface UpdateEvaluationDecisionRequest {
  itemId: string;
  decision: EvaluationDecision;
  notes?: string;
}

export interface ContactRequest {
  id: string;
  recruiterId: string;
  candidateUserId: string;
  message: string;
  status: ContactRequestStatus;
  companyName?: string;
  roleTitle?: string;
  responseMessage?: string;
  respondedAt?: string;
  expiresAt?: string;
  creditsUsed: number;
  createdAt: string;
  updatedAt: string;
}

export interface ContactRequestsResponse {
  items: ContactRequest[];
  total: number;
}

export interface CreateContactRequestParams {
  candidateUserId: string;
  message: string;
  roleTitle?: string;
}

export interface RespondToContactRequestParams {
  requestId: string;
  status: 'accepted' | 'declined';
  responseMessage?: string;
}

export interface RecruiterDashboardStats {
  savedCandidatesCount: number;
  activeEvaluationsCount: number;
  pendingContactsCount: number;
  acceptedContactsCount: number;
  totalCreditsUsed: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const recruiterApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // =====================================================
    // Saved Candidates
    // =====================================================

    // Save a candidate
    saveCandidate: builder.mutation<SavedCandidate, SaveCandidateRequest>({
      query: (body) => ({
        url: '/recruiter/saved-candidates',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<SavedCandidate>) => response.data,
      invalidatesTags: ['SavedCandidates'],
    }),

    // Unsave a candidate
    unsaveCandidate: builder.mutation<void, string>({
      query: (candidateUserId) => ({
        url: `/recruiter/saved-candidates/${candidateUserId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SavedCandidates'],
    }),

    // List saved candidates
    listSavedCandidates: builder.query<SavedCandidatesResponse, { limit?: number; offset?: number; tags?: string[] }>({
      query: (params) => ({
        url: '/recruiter/saved-candidates',
        params,
      }),
      transformResponse: (response: ApiResponse<SavedCandidatesResponse>) => response.data,
      providesTags: ['SavedCandidates'],
    }),

    // Check if candidate is saved
    checkCandidateSaved: builder.query<{ isSaved: boolean }, string>({
      query: (candidateUserId) => `/recruiter/saved-candidates/${candidateUserId}/check`,
      transformResponse: (response: ApiResponse<{ isSaved: boolean }>) => response.data,
      providesTags: (_result, _error, candidateUserId) => [{ type: 'SavedCandidates', id: candidateUserId }],
    }),

    // =====================================================
    // Evaluation Sessions
    // =====================================================

    // Create evaluation session
    createEvaluationSession: builder.mutation<EvaluationSession, CreateEvaluationSessionRequest>({
      query: (body) => ({
        url: '/recruiter/evaluations',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<EvaluationSession>) => response.data,
      invalidatesTags: ['Evaluations'],
    }),

    // List evaluation sessions
    listEvaluationSessions: builder.query<EvaluationSessionsResponse, { status?: EvaluationSessionStatus; limit?: number; offset?: number }>({
      query: (params) => ({
        url: '/recruiter/evaluations',
        params,
      }),
      transformResponse: (response: ApiResponse<EvaluationSessionsResponse>) => response.data,
      providesTags: ['Evaluations'],
    }),

    // Get evaluation session with items
    getEvaluationSession: builder.query<EvaluationSessionWithItems, string>({
      query: (sessionId) => `/recruiter/evaluations/${sessionId}`,
      transformResponse: (response: ApiResponse<EvaluationSessionWithItems>) => response.data,
      providesTags: (_result, _error, sessionId) => [{ type: 'Evaluations', id: sessionId }],
    }),

    // Update evaluation decision
    updateEvaluationDecision: builder.mutation<EvaluationItem, UpdateEvaluationDecisionRequest>({
      query: ({ itemId, decision, notes }) => ({
        url: `/recruiter/evaluations/items/${itemId}`,
        method: 'PATCH',
        body: { decision, notes },
      }),
      transformResponse: (response: ApiResponse<EvaluationItem>) => response.data,
      invalidatesTags: ['Evaluations'],
    }),

    // Complete evaluation session
    completeEvaluationSession: builder.mutation<EvaluationSession, string>({
      query: (sessionId) => ({
        url: `/recruiter/evaluations/${sessionId}/complete`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<EvaluationSession>) => response.data,
      invalidatesTags: (_result, _error, sessionId) => ['Evaluations', { type: 'Evaluations', id: sessionId }],
    }),

    // List sent contact requests
    listSentContactRequests: builder.query<ContactRequestsResponse, { status?: ContactRequestStatus; limit?: number; offset?: number }>({
      query: (params) => ({
        url: '/recruiter/contacts/sent',
        params,
      }),
      transformResponse: (response: ApiResponse<ContactRequestsResponse>) => response.data,
      providesTags: ['ContactRequests'],
    }),

    // =====================================================
    // Dashboard
    // =====================================================

    // Get recruiter dashboard stats
    getRecruiterDashboardStats: builder.query<RecruiterDashboardStats, void>({
      query: () => '/recruiter/dashboard',
      transformResponse: (response: ApiResponse<RecruiterDashboardStats>) => response.data,
      providesTags: ['RecruiterDashboard'],
    }),
  }),
  overrideExisting: false,
});

export const {
  // Saved Candidates
  useSaveCandidateMutation,
  useUnsaveCandidateMutation,
  useListSavedCandidatesQuery,
  useCheckCandidateSavedQuery,
  // Evaluations
  useCreateEvaluationSessionMutation,
  useListEvaluationSessionsQuery,
  useGetEvaluationSessionQuery,
  useUpdateEvaluationDecisionMutation,
  useCompleteEvaluationSessionMutation,
  // Contact Requests (Recruiter)
  useListSentContactRequestsQuery,
  // Dashboard
  useGetRecruiterDashboardStatsQuery,
} = recruiterApi;
